import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, KeyRound } from 'lucide-react';
import type { LobbyTableInfo } from '@draughts/shared';
import { canJoinMatchType } from '@draughts/shared';
import { useOnlineStore, type OnlineUser } from '../../store/online-store';
import { useAuthStore } from '../../store/auth-store';
import { useLobbyStore } from '../../store/lobby-store';
import { usePreferencesStore } from '../../store/preferences-store';
import { mappaTavoliPerHost, tavoloDi } from '../../utils/tavolo-aperto';
import { variantPill } from '../../utils/variant-styling';
import { variantShortLabel } from '../../utils/variant-labels';
import { cadenceIcon, parseTimeControl } from '../../utils/cadence-icons';
import { matchTypeLabel } from '../../utils/challenge-labels';
import { trackTableSeated } from '../../utils/analytics';
import type { RatingBucket } from '../../utils/rating-bucket';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { log } from '../../utils/logger';
import OnlinePlayersList from './OnlinePlayersList';
import PlayerProfileOverlay from '../profile/PlayerProfileOverlay';
import '../dashboard/PlayModals.css';
import './ConnectedUsersModal.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Sfida l'avversario scelto. Il chiamante apre la Sfida diretta già puntata. */
  onChallenge: (user: OnlineUser) => void;
  /**
   * Siediti al tavolo che l'avversario ha già aperto: `true` se la richiesta è
   * andata, `false` se il server l'ha rifiutata (il perché finisce nell'`error`
   * del lobby store, che il riquadro mostra).
   *
   * Opzionale: senza di lei il bottone «Sfida» fa quello che ha sempre fatto —
   * una sfida diretta — e nessuna scelta compare. Così i posti che non possono
   * far partire una partita (e i test che non la passano) restano come prima.
   */
  onSitAtTable?: (tableId: string) => Promise<boolean>;
  /** Guarda la partita di chi sta giocando. */
  onSpectate?: (user: OnlineUser) => void;
  /**
   * Di quale punteggio parla la lista: variante + cadenza.
   *
   * Chi ha una card dei punteggi sotto gli occhi (la dashboard) passa LA SUA
   * scelta: la lista dei collegati e la card devono dire la stessa cosa, o il
   * numero accanto al nome misura un'altra partita. Chi quella card non ce l'ha
   * (la home) non passa niente, e vale il ripiego sulle preferenze.
   */
  ratingBucket?: RatingBucket;
  /**
   * Apre il modale d'accesso: la maniglia dell'invito qui sotto. Opzionale
   * perché la dashboard, che si monta solo per un registrato non ospite, quel
   * riquadro non lo mostra mai e non ha nessuna porta da passare.
   */
  onSignIn?: () => void;
  /**
   * Da pannello e non da finestra: il velo, la trappola del fuoco, la voce di
   * cronologia e l'Escape sono di chi ci sta attorno (`.coppia-gioca` di
   * PlayOverlay), non suoi.
   *
   * Due trappole del fuoco attive insieme non convivono: quella dell'altro
   * pannello vede il fuoco «fuori» da se' e a ogni TAB se lo strappa, quindi
   * tabulare a sinistra buttava a destra e viceversa. Per lo stesso motivo niente
   * seconda voce sullo stack del gesto «indietro» — la coppia si chiude come una
   * cosa sola, con un «indietro» solo.
   */
  affiancato?: boolean;
}

/**
 * Chi è collegato adesso — tutti: registrati, ospiti e (travestiti da ospiti)
 * i bot. Prima una lista così non esisteva: ce n'era una parziale sepolta dentro
 * Sfida diretta che mostrava i soli registrati, perché nasceva per rispondere a
 * "chi posso sfidare" e non a "chi c'è".
 *
 * Veste `play-online-modal`, la stessa di Gioca e Sfida AI: foglio a tutto
 * schermo sotto i 767px, card sul desktop.
 */
export default function ConnectedUsersModal({
  isOpen,
  onClose,
  onChallenge,
  onSitAtTable,
  onSpectate,
  onSignIn,
  ratingBucket,
  affiancato,
}: Props) {
  const { t } = useTranslation();
  /*
   * Quanti se ne vedono in lista, non i contatori del server: `getCounts()`
   * conta anche chi sta guardando mentre `getOnlineUsersExcept` lo esclude, e
   * un badge sopra una lista deve dire quante righe ci sono sotto.
   * La pillola della hero adesso dice lo stesso numero — la sottrazione la fa
   * `useConnectedCount` sul client, perché il contatore viaggia in broadcast e
   * il server non può toglierne uno per destinatario. Prima le due cifre
   * differivano sempre di uno, e la differenza era dichiarata voluta proprio
   * qui.
   */
  const onlineCount = useOnlineStore((s) => s.onlineUsers.length);
  /*
   * Il ripiego, per chi apre questo pannello da una schermata senza card dei
   * punteggi: la variante e la cadenza preferite di chi guarda. Non e' una
   * scelta arbitraria — sono le stesse con cui la dashboard si semina la prima
   * volta — e la testata della lista le dichiara comunque.
   */
  const preferredVariant = usePreferencesStore((s) => s.preferredVariant);
  const preferredCadence = usePreferencesStore((s) => s.preferredCadence);
  const bucketMostrato = useMemo(
    () => ratingBucket ?? { variant: preferredVariant, cadence: preferredCadence },
    [ratingBucket, preferredVariant, preferredCadence],
  );
  const isGuest = useAuthStore((s) => s.isGuest);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [profileUsername, setProfileUsername] = useState<string | null>(null);
  /*
   * Il nodo in cui la lista disegna il suo conto alla rovescia, da affiancati.
   *
   * Uno stato e non un `useRef`: il nodo esiste solo DOPO la prima pittura, e un
   * ref non farebbe ridisegnare niente — il portale della lista non saprebbe mai
   * di avere una casa. Il setter di `useState` regge anche il doppio giro di
   * StrictMode, che invoca il `ref` con `null` e poi col nodo.
   */
  const [nodoConto, setNodoConto] = useState<HTMLElement | null>(null);
  /** Un ospite ha provato a sfidare: si spiega perché non può, non si tace. */
  const [showGuestNotice, setShowGuestNotice] = useState(false);
  /*
   * La scelta aperta: chi si vuole affrontare, e il tavolo che aveva aperto nel
   * momento in cui lo si è premuto.
   *
   * Il tavolo si PORTA DIETRO, non si rilegge dalla lista a ogni render: un
   * «Siediti» rifiutato perché il tavolo è appena stato preso fa ritirare quel
   * tavolo anche lato server, quindi un attimo dopo non è più in lista — e un
   * riquadro che si legge dalla lista svanirebbe portandosi via il messaggio
   * d'errore che spiegava cosa è successo.
   */
  const [scelta, setScelta] = useState<{ user: OnlineUser; table: LobbyTableInfo } | null>(null);
  /*
   * Il fuoco entra nel riquadro UNA volta, all'apertura.
   *
   * Serve perche' il riquadro prende il posto della riga: il bottone che si e'
   * premuto si smonta, e senza questo il fuoco cadrebbe sul `<body>`. Il fuoco va
   * al contenitore e non al primo bottone — la ricetta di `useModaleAccessibile`:
   * cosi' uno screen reader legge prima cos'e' questa cosa che si e' aperta.
   * In regalo, `focus()` lo porta in vista da se', che su un foglio da telefono
   * e' esattamente cio' che serviva.
   *
   * La bandiera sta QUI e non in un effetto dentro il riquadro: il riquadro si
   * rimonta quando la lista smette di disegnare l'elenco (presenza scaduta, lista
   * svuotata) o quando passa accanto a una riga unita, e un effetto di montaggio
   * gli ridarebbe il fuoco a ogni spostamento, strappandolo a chi stava
   * tabulando altrove.
   */
  const fuocoDato = useRef(false);
  /* L'id del titolo, da `useId` e non scritto a mano: di questo pannello possono
     esserci due istanze montate insieme (quella affiancata a «Gioca» e quella
     della dashboard), e due id uguali fanno puntare un nome accessibile
     all'elemento sbagliato. */
  const idTitolo = useId();

  /* I tavoli aperti per nome dell'host: la stessa lettura che fa la lista per
     dipingere il badge «In attesa». Si seleziona l'array, non una Map nuova. */
  const tables = useLobbyStore((s) => s.tables);
  const lobbyBusy = useLobbyStore((s) => s.isBusy);
  const lobbyError = useLobbyStore((s) => s.error);
  const tavoliPerHost = useMemo(() => mappaTavoliPerHost(tables), [tables]);

  useOverlayBackButton(isOpen && !affiancato, onClose);

  /* Il fuoco dentro, Tab che non esce, Escape che chiude: vedi `useModaleAccessibile`.
   * Sta QUI e non più in fondo, sotto il `return null`: là il modale chiuso chiamava un
   * hook in meno di quello aperto, e all'apertura React lanciava «Rendered more hooks
   * than during the previous render», cioè l'app intera finiva sulla schermata d'errore.
   * L'argomento è `isOpen` e non `true` perché è il parametro `attivo` dell'hook, che a
   * modale chiuso non fa niente. */
  const riferimentoModale = useModaleAccessibile<HTMLDivElement>(isOpen && !affiancato, onClose);

  if (!isOpen) return null;

  const handleChallenge = (user: OnlineUser) => {
    /*
     * Chi ha un tavolo aperto sta GIÀ aspettando un avversario: mandargli una
     * sfida diretta è una seconda offerta per la stessa partita, e la più
     * veloce — sedersi — resta lì. Quindi si chiede quale delle due si vuole.
     *
     * Il controllo dell'ospite viene dopo di proposito: sedersi a un tavolo un
     * ospite PUÒ farlo (lo fa già dalla lobby), sfidare no.
     */
    const tavolo = tavoloDi(tavoliPerHost, user.username);
    if (onSitAtTable && tavolo) {
      // L'`error` del lobby store è condiviso con gli altri gesti (apri tavolo,
      // annulla, siediti dalla lista): senza questo, un guasto di prima
      // comparirebbe dentro un riquadro appena aperto.
      useLobbyStore.getState().setError(null);
      fuocoDato.current = false;
      setScelta({ user, table: tavolo });
      return;
    }
    // Il divieto è anche lato server (`GUEST_CANNOT_CHALLENGE`); qui serve a
    // non far partire una richiesta destinata a essere rifiutata, e soprattutto
    // a dire perché. Nascondere il pulsante lascerebbe l'ospite senza capire.
    if (isGuest) {
      log.info('ConnectedUsersModal', 'guest tried to challenge');
      setShowGuestNotice(true);
      return;
    }
    onChallenge(user);
  };

  const handleSit = async () => {
    if (!scelta || !onSitAtTable) return;
    const { table } = scelta;
    log.info('ConnectedUsersModal', 'siediti al tavolo del giocatore', { tableId: table.id });
    trackTableSeated(table.variant, table.timeControl);
    const esito = await onSitAtTable(table.id);
    /*
     * Se è andata non si chiude niente: il tavolo preso fa partire la partita,
     * App cambia schermata e da lì il foglio se ne va da sé (l'effetto su
     * `[screen]` per il pannello affiancato, lo smontaggio della dashboard per
     * la pastiglia). Se è stata rifiutata il riquadro resta aperto: ci sta
     * sopra il messaggio che dice perché.
     */
    if (esito) setScelta(null);
  };

  /* La sfida diretta, dall'interno della scelta: la stessa porta di sempre,
     divieto dell'ospite compreso. */
  const handleChallengeInstead = () => {
    const user = scelta?.user;
    setScelta(null);
    if (!user) return;
    if (isGuest) {
      log.info('ConnectedUsersModal', 'guest tried to challenge');
      setShowGuestNotice(true);
      return;
    }
    onChallenge(user);
  };

  /*
   * La maniglia dell'invito. Sotto i 767px questo è un foglio a tutto schermo:
   * copre la barra di navigazione, e con lei l'unico «Accedi» dell'app. Un
   * invito ad accedere sopra la porta d'accesso nascosta è un vicolo cieco,
   * proprio dove l'app si usa di più. Resta una porta sola e un nome solo —
   * `auth.signIn`, come navbar e Arene — che qui si limita a spuntare.
   *
   * Non si chiude niente: il modulo d'accesso sale sopra i fogli da sé
   * (`.auth-modal-overlay` sta a 10500, vedi AuthModal.css). Sparecchiare prima
   * significherebbe chiudere DUE overlay — questo e la lobby sotto — nello
   * stesso commit, e ogni chiusura programmatica consuma una voce di
   * cronologia (useOverlayBackButton): il gesto «indietro» successivo veniva
   * inghiottito e usciva dall'app invece di chiudere l'accesso.
   */
  const signInButton = onSignIn && (
    <button type="button" className="guest-notice-login" onClick={onSignIn}>
      <KeyRound size={16} aria-hidden="true" />
      {t('auth.signIn')}
    </button>
  );

  /*
   * «Siediti al suo tavolo» oppure «sfidalo a modo tuo».
   *
   * Un riquadro dentro il modale e non un secondo velo: questo pannello a volte
   * è già affiancato a «Gioca» dentro un velo solo, e ogni overlay in più è
   * un'altra voce di cronologia da restituire al gesto «indietro».
   */
  const riquadroTavolo = (() => {
    if (!scelta) return null;
    const { table } = scelta;
    const cadenza = parseTimeControl(table.timeControl);
    // Il ripiego è quello di `LobbyTableRow`: un tavolo pubblicato da un client
    // su un bundle in cache non porta `matchType`.
    const matchType = table.matchType ?? (table.rated ? 'any' : 'casual');
    // «Solo valida»: chi non muove punteggi non si siede a un tavolo che ne
    // promette il movimento. È la stessa regola che il server applica
    // (`REGISTERED_ONLY`), detta prima del viaggio.
    const sbarrato = !canJoinMatchType(matchType, { isGuest });
    return (
      <div
        className="table-choice"
        role="group"
        /* Il nome del gruppo e' il suo titolo, non una copia del titolo: con un
           `aria-label` identico al paragrafo, mettendo il fuoco qui la stessa
           frase si sentiva due volte. */
        aria-labelledby={idTitolo}
        /* Fuori dal giro di Tab (lo e' per `useModaleAccessibile`), ma capace di
           prendere il fuoco quando glielo si da'. */
        tabIndex={-1}
        ref={(el) => {
          if (!el || fuocoDato.current) return;
          fuocoDato.current = true;
          el.focus();
        }}
      >
        <p className="table-choice-title" id={idTitolo}>
          {t('online.challenge.tableOpen.title', { username: scelta.user.username })}
        </p>
        <div className="table-choice-chips">
          <span className="table-choice-chip">
            {variantPill(table.variant).flag}
            {variantShortLabel(t, table.variant)}
          </span>
          <span className="table-choice-chip">
            {cadenza && cadenceIcon(cadenza.category, 12)}
            {cadenza ? cadenza.clock : t('game.timeControls.noTimeLimit')}
          </span>
          <span className="table-choice-chip">{matchTypeLabel(t, table.matchType, table.rated)}</span>
        </div>
        {/* Un ospite a un tavolo «qualsiasi» entra, ma la partita non conta: lo
            si dice accanto al bottone, non in un terzo passo di conferma — il
            riquadro È già la conferma. */}
        {!sbarrato && isGuest && matchType === 'any' && (
          <p className="table-choice-note">{t('online.challenge.link.guestCasualNote')}</p>
        )}
        {sbarrato ? (
          <p className="table-choice-barred">{t('online.challenge.registeredOnly')}</p>
        ) : (
          <button
            type="button"
            className="table-choice-action is-sit"
            disabled={lobbyBusy}
            onClick={() => { void handleSit(); }}
          >
            {t('lobby.sit')}
          </button>
        )}
        <button type="button" className="table-choice-action" onClick={handleChallengeInstead}>
          {t('online.challenge.tableOpen.other')}
        </button>
        <button type="button" className="table-choice-action is-cancel" onClick={() => setScelta(null)}>
          {t('common.cancel')}
        </button>
        {/* Perché il «Siediti» è stato rifiutato: tavolo appena preso, host
            entrato in partita altrove, o si è già in partita. Non da affiancati:
            là la stessa riga la disegna già «Gioca» accanto (`.lobby-error`), e
            due volte sembrerebbero due guasti. */}
        {!affiancato && lobbyError && (
          <p className="table-choice-error" role="alert">{t(lobbyError)}</p>
        )}
      </div>
    );
  })();

  /* Il profilo di un giocatore si porta il suo portale (`FullscreenPanel`), quindi
     da affiancati non diventa un item della riga flex: resta fratello del pannello
     e sale sopra tutto da se'. */
  const profilo = profileUsername && (
    <PlayerProfileOverlay username={profileUsername} onClose={() => setProfileUsername(null)} />
  );

  const pannello = (
    <div
      ref={affiancato ? undefined : riferimentoModale}
      className="modal dashboard-modal play-online-modal connected-users-modal"
      /* `group` e non `dialog` da affiancati: in un velo c'e' UN dialogo —
         l'involucro della coppia — e questo e' un riquadro di comandi dentro di lui.
         Due `aria-modal` fratelli direbbero a uno screen reader che ci sono due
         finestre modali sovrapposte, che non e' quello che si vede. */
      role={affiancato ? 'group' : 'dialog'}
      aria-modal={affiancato ? undefined : true}
      aria-label={t('online.onlinePlayers')}
      tabIndex={affiancato ? undefined : -1}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="play-online-head">
        {/* Il totale sta QUI, accanto al titolo, e non più in una riga a parte
            sotto: prima l'intestazione era tre righe (titolo, «x registrati ·
            y ospiti», e il titolino della lista col suo badge) prima di far
            vedere un solo giocatore. Nascosto a zero, che è anche lo stato di
            caricamento e quello del visitatore, che la lista non la vede. */}
        <h2>
          {onlineCount > 0 && <span className="connected-head-count">{onlineCount}</span>}
          {/* In un elemento suo e non testo nudo: l'`<h2>` e' un flex (per il
              badge), e su un contenitore flex `text-overflow` non fa niente.
              Serve alla rete dell'ellissi in ConnectedUsersModal.css — se un
              giorno il titolo non ci sta, deve troncare e non andare a capo,
              perche' le due testate affiancate sono alte uguali per contratto. */}
          <span className="connected-head-title">{t('online.onlinePlayers')}</span>
        </h2>
        {/* Da affiancati il conto alla rovescia della lista sale qui, a sinistra
            della X: era la terza riga di una testata interna che ripeteva questo
            stesso titolo. Qui c'e' solo la CASA; chi lo disegna, e chi lo fa
            battere, e' la lista. */}
        {affiancato && <span className="connected-head-conto" ref={setNodoConto} />}
        {/* Una X sola, e sta QUI anche da affiancati: la coppia e' una card
            sola, e questa testata ne e' la meta' destra, cioe' il suo angolo in
            alto a destra. Dal foglio di «Gioca» la X e' sparita di proposito
            (PlayOverlay.tsx) — la', in mezzo alla card, nessuno la cerca.
            Chiude la coppia e non questo pannello: `onClose`, da affiancati, e'
            quello che spegne tutte e due le meta' (App.tsx, DashboardPage.tsx). */}
        <button type="button" className="play-online-close" aria-label={t('common.cancel')} onClick={onClose}>
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      <div className="play-online-body">
        {/* Un visitatore non vede la lista (`OnlinePlayersList` si chiude da
            sé): al suo posto l'invito, con la sua maniglia. Il doppione da
            evitare era «Registrati», che sta dentro il modale d'accesso: qui
            c'è solo la porta, la stessa della navbar che il foglio copre. */}
        {!isAuthenticated ? (
          <div className="guest-challenge-notice">
            <p>{t('online.challenge.loginRequired')}</p>
            {signInButton}
          </div>
        ) : (
          <>
            {showGuestNotice && (
              <div className="guest-challenge-notice" role="alert">
                <p>{t('online.challenge.guestCannotChallenge')}</p>
                {signInButton}
              </div>
            )}

            {/* Il riquadro della scelta non sta piu' qui: lo disegna la lista, al
                posto della riga di quel giocatore. Stava in cima — cioe' spesso
                sopra la piega, a parecchie righe di distanza dal dito — e si
                premeva «Sfida» senza vedere niente accadere. */}
            <OnlinePlayersList
              onChallengePlayer={handleChallenge}
              onSpectatePlayer={onSpectate}
              onViewProfile={(user) => setProfileUsername(user.username)}
              hideCount
              ratingBucket={bucketMostrato}
              testataAltrove={affiancato ? { conto: nodoConto } : undefined}
              rigaEspansa={scelta && { userId: scelta.user.userId, contenuto: riquadroTavolo }}
            />
          </>
        )}
      </div>
    </div>
  );

  /* Da affiancati il velo e' di chi ci sta attorno: qui si rende il solo pannello.
     Overlay e non `setScreen('profile')` per il profilo: così funziona anche sopra
     una partita in corso, dove App riporterebbe subito alla scacchiera. */
  if (affiancato) {
    return (
      <>
        {pannello}
        {profilo}
      </>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      {pannello}
      {profilo}
    </div>
  );
}
